import { useEffect, useState } from "react";
import { Form, Input, Row, Col, Button, Select, InputNumber } from "antd";
import StepHeader from "./StepsHeader";
import UploadDoc from "./UploadDoc";
import { QUANTITY_UNITS } from "../../../../../../utils/constantData";

const { TextArea } = Input;
const { Option } = Select;

export default function AdditionalDetailsStep({
  onBack,
  onNext,
  onCancel,
  initialData = {},
  projectData,
  projectConfigLoading,
}) {
  const [form] = Form.useForm();
  const [documents, setDocuments] = useState([]);

  const requiredDocsForProject =
    projectData?.projectConfig?.projectDocuments || [];
  const fieldsBaseOnConfig = projectData?.projectConfig?.metadataFields || {};

  // Initialize form with saved data
  useEffect(() => {
    if (initialData) {
      setDocuments(initialData.documents || []);
    }
  }, [initialData, form]);

  // Sync documents state with form when documents change
  useEffect(() => {
    form.setFieldsValue({ documents });
  }, [documents, form]);

  const validateUnitRequired = ({ getFieldValue }) => ({
    validator(_, value) {
      const quantity = getFieldValue("quantity");
      if (quantity && !value) {
        return Promise.reject(
          new Error("Unit is required when quantity is specified")
        );
      }
      return Promise.resolve();
    },
  });

  const validateRequiredDocuments = () => ({
    validator: (_, value) => {
      const requiredDocs = requiredDocsForProject
        .filter((doc) => doc.required)
        .map((doc) => doc._id);

      const missingDocs = requiredDocs.filter((docId) => {
        const uploadedDocIds = Array.isArray(value)
          ? value.map((doc) => doc._id)
          : [];
        return !uploadedDocIds.includes(docId);
      });

      if (missingDocs.length > 0) {
        const missingNames = requiredDocsForProject
          .filter((doc) => missingDocs.includes(doc._id))
          .map((doc) => doc.documentName);
        return Promise.reject(
          new Error(`Please upload: ${missingNames.join(", ")}`)
        );
      }
      return Promise.resolve();
    },
  });

  const handleNext = () => {
    form
      .validateFields()
      .then((formValues) => {
        onNext(formValues);
      })
      .catch((error) => {
        console.log("Form validation failed:", error);
      });
  };

  const handleBack = () => {
    const formValues = form.getFieldsValue();
    onBack(formValues);
  };

  // Initialize form with saved data
  useEffect(() => {
    if (initialData) {
      form.setFieldsValue(initialData);
    }
  }, [initialData, form]);

  return (
    <div style={{ paddingBottom: 20 }}>
      <StepHeader current={2} />

      <Form layout="vertical" form={form}>
        <Row gutter={16}>
          {fieldsBaseOnConfig?.batchId?.required === true ? (
            <Col span={12}>
              <Form.Item
                label="Batch ID"
                name="batchId"
                rules={[
                  {
                    required: fieldsBaseOnConfig?.batchId?.mandatory === true,
                    message: "Please enter batch ID",
                  },
                ]}
              >
                <Input placeholder="Enter batch ID" />
              </Form.Item>
            </Col>
          ) : null}

          {fieldsBaseOnConfig?.supplier?.required === true ? (
            <Col span={12}>
              <Form.Item
                label="Supplier"
                name="supplier"
                rules={[
                  {
                    required: fieldsBaseOnConfig?.supplier?.mandatory === true,
                    message: "Please enter supplier name",
                  },
                ]}
              >
                <Input placeholder="Enter supplier name" />
              </Form.Item>
            </Col>
          ) : null}

          {fieldsBaseOnConfig?.quantity?.required === true ? (
            <Col span={16}>
              <Form.Item
                label="Quantity"
                name="quantity"
                rules={[
                  {
                    required: fieldsBaseOnConfig?.quantity?.mandatory === true,
                    message: "Please enter quantity",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter quantity"
                  style={{ width: "100%" }}
                  min={0}
                  precision={2}
                />
              </Form.Item>
            </Col>
          ) : null}

          {fieldsBaseOnConfig?.quantityUnit?.required === true ? (
            <Col span={8}>
              <Form.Item
                label="Unit"
                name="quantityUnit"
                initialValue=""
                rules={[
                  validateUnitRequired,
                  {
                    required:
                      fieldsBaseOnConfig?.quantityUnit?.mandatory === true,
                    message: "Please select unit",
                  },
                ]}
              >
                <Select placeholder="Select unit" allowClear options={QUANTITY_UNITS} />
              </Form.Item>
            </Col>
          ) : null}

          {fieldsBaseOnConfig?.notes?.required === true ? (
            <Col span={24}>
              <Form.Item
                label="Additional Notes"
                name="notes"
                rules={[
                  {
                    required: fieldsBaseOnConfig?.notes?.mandatory === true,
                    message: "Please enter additional notes",
                  },
                ]}
              >
                <TextArea
                  rows={5}
                  placeholder="Enter any additional information..."
                />
              </Form.Item>
            </Col>
          ) : null}
        </Row>

        {/* Document Upload */}
        {requiredDocsForProject?.length > 0 && (
          <Form.Item
            label="Required Documents"
            name="documents"
            rules={[validateRequiredDocuments()]}
          >
            <UploadDoc
              documents={requiredDocsForProject}
              value={documents}
              onChange={setDocuments}
            />
          </Form.Item>
        )}
      </Form>

      <div
        style={{
          borderTop: "1px solid #eee",
          marginTop: 30,
          paddingTop: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button onClick={handleBack}>Back</Button>

        <div style={{ display: "flex", gap: 10 }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleNext}
            style={{ padding: "0 28px" }}
          >
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}
